import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  Compass,
  ClipboardList,
  MoreHorizontal,
  Plus,
  PlusCircle,
  BarChart3,
  Trophy,
  Radio,
  Sparkles,
  X,
  ChevronRight,
} from "lucide-react";
import { PRIMARY_NAV_ITEMS } from "../../navigation/tspMenu";

// Renders an icon matching the tab key, preserving clean line weights
function getNavIcon(key, size = 22, strokeWidth = 1.8) {
  switch (key) {
    case "dashboard":
      return <LayoutGrid size={size} strokeWidth={strokeWidth} />;
    case "journeys":
      return <Compass size={size} strokeWidth={strokeWidth} />;
    case "journey-list":
      return <ClipboardList size={size} strokeWidth={strokeWidth} />;
    case "add-journey":
      return <PlusCircle size={size} strokeWidth={strokeWidth} />;
    case "your-stats":
      return <BarChart3 size={size} strokeWidth={strokeWidth} />;
    case "live-ranking":
      return <Trophy size={size} strokeWidth={strokeWidth} />;
    case "live-tracking":
      return <Radio size={size} strokeWidth={strokeWidth} />;
    case "success-stories":
      return <Sparkles size={size} strokeWidth={strokeWidth} />;
    default:
      return <Compass size={size} strokeWidth={strokeWidth} />;
  }
}

/**
 * Mobile Bottom Navigation Bar (Phone Navigation Menu)
 * Matches the reference design:
 * - 3 items shown directly on the bar: Dashboard, Journey, Journey List
 * - 4th item: "More" button with 3 dots (...)
 * - Clicking "More" opens a slide-up bottom sheet with the remaining items
 *   (Add Journey, Your Stats, Live Ranking, Live Tracking, Success Stories)
 * - Highlights "More" whenever the current active route is inside the "More" list
 */
export default function TspBottomNav() {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // Split into first 3 items and remaining items
  const visibleItems = PRIMARY_NAV_ITEMS.slice(0, 3);
  const moreItems = PRIMARY_NAV_ITEMS.slice(3);

  // Check if any of the "More" items is currently active
  const isMoreActive = moreItems.some((item) => item.isActive(location.pathname));

  // Automatically close "More" drawer upon route change
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  // Close on Escape key press
  useEffect(() => {
    if (!moreOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [moreOpen]);

  // Prevent background scroll when bottom sheet is open
  useEffect(() => {
    if (moreOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [moreOpen]);

  return (
    <>
      {/* Floating Action Button for "Add Journey" (Phone only, matching reference design) */}
      <Link
        to="/tsp/journeys/new"
        className="tsp-bottom-nav-fab"
        aria-label="Add Journey"
        title="Add Journey"
      >
        <Plus size={26} strokeWidth={2.5} />
      </Link>

      <nav className="tsp-bottom-nav" aria-label="TSP phone navigation">
        <ul className="tsp-bottom-nav-list">
          {visibleItems.map((item) => {
            const active = item.isActive(location.pathname);
            return (
              <li key={item.key} className="tsp-bottom-nav-li">
                <Link
                  to={item.route}
                  className={`tsp-bottom-nav-item ${active ? "active" : ""}`}
                  aria-current={active ? "page" : undefined}
                >
                  <span className="tsp-bottom-nav-icon">
                    {getNavIcon(item.key, 22, 1.8)}
                  </span>
                  <span className="tsp-bottom-nav-label">{item.label}</span>
                </Link>
              </li>
            );
          })}

          {/* 4th Tab: "More" */}
          <li className="tsp-bottom-nav-li">
            <button
              type="button"
              className={`tsp-bottom-nav-item ${isMoreActive || moreOpen ? "active" : ""}`}
              onClick={() => setMoreOpen((prev) => !prev)}
              aria-expanded={moreOpen}
              aria-controls="tsp-more-sheet"
              aria-label="More navigation options"
            >
              <span className="tsp-bottom-nav-icon">
                <MoreHorizontal size={22} strokeWidth={2} />
              </span>
              <span className="tsp-bottom-nav-label">More</span>
            </button>
          </li>
        </ul>
      </nav>

      {/* Slide-up "More" Bottom Sheet & Backdrop */}
      {moreOpen && (
        <div
          className="tsp-bottom-sheet-backdrop"
          onClick={() => setMoreOpen(false)}
          aria-hidden="true"
        />
      )}

      {moreOpen && (
        <div
          id="tsp-more-sheet"
          className="tsp-bottom-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="More navigation"
        >
          <div className="tsp-bottom-sheet-handle" />
          <div className="tsp-bottom-sheet-header">
            <h3 className="tsp-bottom-sheet-title">More Navigation</h3>
            <button
              type="button"
              className="tsp-bottom-sheet-close"
              onClick={() => setMoreOpen(false)}
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>

          <ul className="tsp-bottom-sheet-list">
            {moreItems.map((item) => {
              const active = item.isActive(location.pathname);
              return (
                <li key={item.key}>
                  <Link
                    to={item.route}
                    className={`tsp-bottom-sheet-link ${active ? "active" : ""}`}
                    onClick={() => setMoreOpen(false)}
                    aria-current={active ? "page" : undefined}
                  >
                    <span className="tsp-bottom-sheet-icon">
                      {getNavIcon(item.key, 20, 1.8)}
                    </span>
                    <span className="tsp-bottom-sheet-text">
                      <span className="tsp-bottom-sheet-label">{item.label}</span>
                    </span>
                    {active ? (
                      <span className="tsp-bottom-sheet-badge">Active</span>
                    ) : (
                      <ChevronRight size={18} className="tsp-bottom-sheet-chevron" />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}
